import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Modal from 'presentation/components/Modal/Modal';
import KSButton from 'presentation/components/KSButton';
import Loading from 'components/Loading';
import LocationSelector from '../../../components/LocationSelector/LocationSelector';
import RoleSelector from '../../../components/RoleSelector/RoleSelector';
import Checkbox from '../../../components/Checkbox/Checkbox';

import { useAddLocationUser } from 'hooks/useAddLocationUser';

import './AddLocationModal.scss';
import { useAuthContext } from 'context/useAuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  onSuccess?: () => void;
}

interface FormValues {
  location: { label: string; value: string } | undefined;
  role: { label: string; value: string } | undefined;
  emailOptOut: boolean;
}

const DEFAULT_FORM_VALUES: FormValues = {
  location: undefined,
  role: undefined,
  emailOptOut: false,
};

const DEFAULT_ROLE = 'Bookkeeper';

export default function AddLocationModal({ isOpen, onClose, email, onSuccess }: Props) {
  const { mutate: addLocationUser, isPending } = useAddLocationUser();
  const { userId } = useAuthContext();

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const handleReset = useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
  }, [reset]);

  const handleCancel = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleSuccess = useCallback(() => {
    handleReset();
    onClose();
    onSuccess?.();
  }, [handleReset, onClose, onSuccess]);

  const handleError = useCallback(() => {
    // Reset form state to clear isSubmitSuccessful flag
    // This prevents LocationSelector's ref callback from clearing values
    reset(undefined, { keepValues: true });
  }, [reset]);

  const onSubmit = useCallback(
    (data: FormValues) => {
      const locationId = data.location?.value;
      if (!locationId || !userId) return;

      addLocationUser(
        {
          userId,
          email,
          locationId,
          emailOptOut: data.emailOptOut,
          role: data.role?.value ?? DEFAULT_ROLE,
        },
        { onSuccess: handleSuccess, onError: handleError }
      );
    },
    [userId, email, addLocationUser, handleSuccess, handleError]
  );

  const isSubmitDisabled = !formState.isValid || !email || !userId || isPending;
  const showUserNotFoundWarning = email && !userId;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <>
          <div className="ks-modal__title">
            <div className="ks-modal__title-main">Add user to a location</div>
          </div>
          <button className="ks-modal__close" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </>
      }
    >
      <div className="users-admin-add-form__wrapper">
        {isPending && (
          <div className="users-admin-add-form__overlay">
            <Loading loadingCircleClass="Loading-circle-medium" />
          </div>
        )}
        <form className="users-admin-container__form users-admin-add-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="users-admin-add-form__intro">
            <p className="users-admin-container__form-title">
              Choose a location and role for <strong>{email}</strong>
            </p>
            {showUserNotFoundWarning && (
              <p className="users-admin-add-form__warning">
                This user does not exist in the system. They must be invited or created first.
              </p>
            )}
          </div>

          <div className="form-grid users-admin-add-form__grid">
            <div className="users-admin-container__form-field">
              <label className="users-admin-container__form-field__label">Location</label>
              <LocationSelector name="location" control={control} rules={{ required: true }} />
            </div>

            <div className="users-admin-container__form-field">
              <label className="users-admin-container__form-field__label">Role</label>
              <RoleSelector name="role" control={control} rules={{ required: true }} />
            </div>

            <div className="users-admin-container__form-field users-admin-add-form__optout">
              <label className="users-admin-container__form-field__label">Email opt-out</label>
              <Controller
                control={control}
                name="emailOptOut"
                render={({ field: { onChange, value } }) => (
                  <Checkbox value={String(value)} onChange={(_, checked) => onChange(checked)} checked={value} />
                )}
              />
            </div>
          </div>

          <div className="ks-modal__footer">
            <KSButton onClick={handleCancel} label="Cancel" color="secondary" size="medium" />
            <KSButton type="submit" disabled={isSubmitDisabled} label="Add location" color="active" />
          </div>
        </form>
      </div>
    </Modal>
  );
}
